import type { SessionResponse } from "../../shared/types/rbac";

/**
 * Shared /auth/session dedup cache.
 *
 * Deliberately dependency-free (the SessionResponse import is type-only, erased
 * at build time). authService must be able to clear this on logout, and
 * authSessionService imports authService — putting the state here keeps that
 * from becoming a circular import.
 *
 * Redirect logout discards module state via full navigation, but logoutPopup()
 * (used in iframes) does not, so the cache must be cleared explicitly or a
 * second user signing in within the TTL would read the first user's session.
 */

let sessionPromise: Promise<SessionResponse> | null = null;
let sessionCachedAt = 0;
const SESSION_CACHE_TTL = 120_000;

/**
 * Run `fetcher` at most once per TTL, sharing the in-flight promise with
 * concurrent callers. Failures are not cached — the next caller retries.
 */
export function dedupeSession(fetcher: () => Promise<SessionResponse>): Promise<SessionResponse> {
  const now = Date.now();
  if (sessionPromise && now - sessionCachedAt < SESSION_CACHE_TTL) {
    return sessionPromise;
  }

  sessionCachedAt = now;
  sessionPromise = fetcher().catch((err) => {
    sessionPromise = null;
    throw err;
  });

  return sessionPromise;
}

/** Drop the cached session. Called on logout and from tests. */
export function clearSessionCache(): void {
  sessionPromise = null;
  sessionCachedAt = 0;
}
